import type { MessageData, PaginatedResponse, SuccessResponse } from "@shared/types";
import apiClient from "@/app/api/client";
import { replaceParams, staffEndpoints } from "@/app/api/endpoints";
import type {
	AssignStaffBrigadeRequest,
	AssignStaffBrigadeResponse,
	CreateStaffRequest,
	ListStaffParams,
	Staff,
	UpdateStaffRequest,
} from "../types";

export const staffService = {
	async list(params: ListStaffParams): Promise<PaginatedResponse<Staff>> {
		const queryParams: Record<string, string | number | boolean | undefined> = {
			organizationId: params.organizationId,
			page: params.page ?? 1,
			limit: params.limit ?? 10,
		};
		if (params.brigadeId) {
			queryParams.brigadeId = params.brigadeId;
		}
		if (params.search) {
			queryParams.search = params.search;
		}
		if (params.type) {
			queryParams.type = params.type;
		}
		if (params.isActive !== undefined) {
			queryParams.isActive = params.isActive;
		}
		if (params.sortBy) {
			queryParams.sortBy = params.sortBy;
		}
		if (params.sortOrder) {
			queryParams.sortOrder = params.sortOrder;
		}

		const response = await apiClient.get<PaginatedResponse<Staff>>(staffEndpoints.list, {
			params: queryParams,
		});
		return response.data;
	},

	async getById(id: string): Promise<Staff> {
		const url = replaceParams(staffEndpoints.getById, { id });
		const response = await apiClient.get<SuccessResponse<Staff>>(url);
		return response.data.data;
	},

	async create(data: CreateStaffRequest): Promise<Staff> {
		const response = await apiClient.post<SuccessResponse<Staff>>(staffEndpoints.create, data);
		return response.data.data;
	},

	async update(id: string, data: UpdateStaffRequest): Promise<Staff> {
		const url = replaceParams(staffEndpoints.update, { id });
		const response = await apiClient.patch<SuccessResponse<Staff>>(url, data);
		return response.data.data;
	},

	async delete(id: string): Promise<void> {
		const url = replaceParams(staffEndpoints.delete, { id });
		await apiClient.delete<SuccessResponse<MessageData>>(url);
	},

	/** RFC-003.4: Assign staff to brigade */
	async assignBrigade(
		staffId: string,
		data: AssignStaffBrigadeRequest
	): Promise<AssignStaffBrigadeResponse> {
		const url = replaceParams(staffEndpoints.assignBrigade, { staffId });
		const response = await apiClient.put<SuccessResponse<AssignStaffBrigadeResponse>>(url, data);
		return response.data.data;
	},
};
